<template>
  <div class="toast-container">
    <TransitionGroup name="toast" tag="div" class="toast-list">
      <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast--${toast.type}`]">
        <div class="toast__icon">
          <component :is="getIcon(toast.type)" />
        </div>

        <div class="toast__content">
          <p class="toast__message">{{ toast.message }}</p>
        </div>

        <button class="toast__close" @click="removeToast(toast.id)" aria-label="Fermer">
          <CloseIcon />
        </button>

        <!-- Barre de progression pour les toasts temporaires -->
        <div
          v-if="!toast.persistent"
          class="toast__progress"
          :style="{ animationDuration: `${toast.duration}ms` }"
        ></div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'

const CheckIcon = () => '✓'
const ErrorIcon = () => '✕'
const WarningIcon = () => '⚠'
const InfoIcon = () => 'ℹ'
const CloseIcon = () => '×'

const toasts = ref([])
let toastStore = null

onMounted(async () => {
  const { useToastStore } = await import('../../stores/toast')
  toastStore = useToastStore()

  toasts.value = toastStore.toasts

  toastStore.$subscribe(() => {
    toasts.value = [...toastStore.toasts]
  })
})

const removeToast = (id) => {
  if (toastStore) {
    toastStore.removeToast(id)
  }
}

const getIcon = (type) => {
  const icons = {
    success: CheckIcon,
    error: ErrorIcon,
    warning: WarningIcon,
    info: InfoIcon,
  }
  return icons[type] || InfoIcon
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
}

.toast-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toast {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: flex-start;
  padding: 16px;
  min-width: 300px;
  max-width: 500px;
  position: relative;
  overflow: hidden;
  pointer-events: auto;
  border-left: 4px solid;
}

.toast--success {
  border-left-color: #10b981;
}

.toast--error {
  border-left-color: #ef4444;
}

.toast--warning {
  border-left-color: #f59e0b;
}

.toast--info {
  border-left-color: #3b82f6;
}

.toast__icon {
  margin-right: 12px;
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
}

.toast--success .toast__icon {
  color: #10b981;
}

.toast--error .toast__icon {
  color: #ef4444;
}

.toast--warning .toast__icon {
  color: #f59e0b;
}

.toast--info .toast__icon {
  color: #3b82f6;
}

.toast__content {
  flex: 1;
}

.toast__message {
  margin: 0;
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
}

.toast__close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  margin-left: 12px;
  padding: 0;
  transition: color 0.2s;
}

.toast__close:hover {
  color: #374151;
}

.toast__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    var(--progress-color, #e5e7eb) 0%,
    var(--progress-color, #e5e7eb) 100%
  );
  animation: progress linear forwards;
}

.toast--success .toast__progress {
  --progress-color: #10b981;
}

.toast--error .toast__progress {
  --progress-color: #ef4444;
}

.toast--warning .toast__progress {
  --progress-color: #f59e0b;
}

.toast--info .toast__progress {
  --progress-color: #3b82f6;
}

@keyframes progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Transitions */
.toast-enter-active {
  transition: all 0.3s ease-out;
}

.toast-leave-active {
  transition: all 0.3s ease-in;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
